import { Children, FC, ReactNode, SyntheticEvent, useEffect, useState } from 'react'

import { Tabs as MuiTabs } from '@mui/material'
import Box from '@mui/material/Box'
import Tab from '@mui/material/Tab'
import { useTheme } from '@mui/material/styles'

import { BG_DARK_GRAY, PRIMARY_YELLOW, PURE_BLACK } from 'utils/customizedTheme'

interface TabPanelProps {
  index: string
  value: string
  children?: ReactNode
  dir?: string
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`swipeable-tabs-${index}`}
      aria-labelledby={`swipeable-tab-${index}`}
      style={{ height: '100%' }}
      {...other}
    >
      {value === index && <>{children}</>}
    </div>
  )
}

function a11yProps(index: string) {
  return {
    id: `swipeable-tab-${index}`,
    'aria-controls': `swipeable-tabs-${index}`,
  }
}

interface IProps {
  children: ReactNode | JSX.Element
  tabKey: string
  colors?: string[]
  onChange?: any
}

const Tabs: FC<IProps> = ({ children, tabKey, colors = [], onChange = null }) => {
  const theme = useTheme()
  const [value, setValue] = useState(tabKey)

  const childrenArray = Children.toArray(children || [])

  const handleChange = (event: SyntheticEvent, newValue: string) => {
    setValue(newValue)
  }

  useEffect(() => {
    if(onChange){
      onChange(value)
    }
  }, [value])


  return (
    <Box className='tabs-container' sx={{ display: 'flex', flexDirection: 'column', backgroundColor: colors.length > 0 ? '#fff' : '' }}>
      <MuiTabs
        value={value}
        onChange={handleChange}
        textColor='primary'
        indicatorColor='primary'
        variant='fullWidth'
        aria-label='swipeable-tabs'
        sx={{
          display: childrenArray.length > 1 ? 'block' : 'none',
          ".MuiTabs-indicator": {
          display: 'none'
        },}}
      >
        {childrenArray.length > 1 &&
          childrenArray.map((child: any, i) => (
            <Tab
              sx={colors.length > 0 ? {
                  borderTop: `4px solid  ${colors[i]}`,
                  borderRadius: '0 !important',
                  backgroundColor: '#f5f6fa',
                  color: '#333',
                  '&.MuiTab-root.Mui-selected': {
                    backgroundColor: '#fff',
                    color: '#333',
                  },
                  '&:hover': {
                    backgroundColor: '#fff',
                  },
                } : {
                color: `${PURE_BLACK} !important` ,
                borderBottom: `1px solid ${PURE_BLACK}`,
                borderBottomLeftRadius: '0 !important',
                borderBottomRightRadius: '0 !important',
                '&.MuiTab-root.Mui-selected': {
                  border: `1px solid ${PURE_BLACK}`,
                  backgroundColor: PRIMARY_YELLOW,
                },
                '&:hover': {
                  backgroundColor: BG_DARK_GRAY,
                },
              }}
              key={child?.props?.tabKey}
              value={child?.props?.tabKey}
              label={child?.props?.tabText}
              icon={child?.props?.tabIcon}
              iconPosition={child?.props?.iconPosition}
              disabled={child?.props?.disabled}
              {...a11yProps(child?.props?.tabKey)}
            />
          ))}
      </MuiTabs>

      <Box style={{ flex: '1 1 auto', marginTop: '2rem' }}>
        {childrenArray.length > 0 &&
          childrenArray.map((child: any, i) => (
            <TabPanel key={i} value={value} index={child?.props?.tabKey} dir={theme.direction}>
              {child}
            </TabPanel>
          ))}
      </Box>
    </Box>
  )
}

export default Tabs
